import { TTicketDetails } from '@daymade/api-contracts';
import clsx from 'clsx';
import ImageWithFallback from 'components/ImageWithFallback';
import { dashboardFolder } from 'constants/Dashboard';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { PartnerDiscounts } from 'pages/my-discounts';
import { TSubscriptions } from 'stores/UserStore';
import DashboardComponentHeader from '../common/DashboardComponentHeader';
import ChoosePlanSection from './ChoosePlanSection';
import ResultsLink from './ResultsLink';
import StatSection from './StatSection';
import TicketEntries from './TicketEntries';

import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import GenericSkeleton from 'components/skeleton/GenericSkeleton';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { LOADING_TIMEOUT } from '../common/constants';
import DrawCountdown from './DrawCounter';

import { WinWithFriendsBadge } from 'components/referrals/2026/win-with-friends-badge';
import {
  groupEntriesByPurchasingAction,
  toLotteryEntries,
} from 'utils/ticket/ticketEntries';
import styles from './EntriesAndStats.module.css';

// --- Constants & Utility ---
const STAT_SECTIONS = {
  TOKENS: {
    title: 'Your Tokens',
    icon: `${dashboardFolder}/token-icon.svg`,
    background: `${dashboardFolder}/TOKENS_LINK_MOBILE.svg`,
    backgroundDesktop: `${dashboardFolder}/TOKENS_LINK_DESKTOP.svg`,
    description: (
      <p>Use tokens to enter raffles, redeem prizes, and plant trees</p>
    ),
    link: '/token-town',
    onlyForSubscribedPlayer: false,
  },
  DISCOUNTS: {
    title: 'Your Discounts',
    icon: `${dashboardFolder}/discount-icon.svg`,
    background: `${dashboardFolder}/DISCOUNTS_LINK_MOBILE.svg`,
    backgroundDesktop: `${dashboardFolder}/DISCOUNTS_LINK_DESKTOP.svg`,

    description: (
      <p>
        Member-only offers like <strong>15% off Virgin Experience Days</strong>{' '}
        and more
      </p>
    ),
    link: '/my-discounts',
    onlyForSubscribedPlayer: true,
  },
  POINTS: {
    title: 'Your Points',
    icon: `${dashboardFolder}/leaderboard-icon.svg`,
    background: `${dashboardFolder}/LEADERBOARD_LINK_MOBILE.svg`,
    backgroundDesktop: `${dashboardFolder}/LEADERBOARD_LINK_DESKTOP.svg`,
    description: (
      <p>
        Each point boosts your <br />
        Leaderboard chances to win
      </p>
    ),
    link: '/leaderboard',
    onlyForSubscribedPlayer: true,
  },
};

// ...existing code...
// --- Main Component ---

interface EntriesAndStatsProps {
  isFtmp: boolean;
  tickets: TTicketDetails[];
  tokenBalance: number;
  leaderboardPoints: number;
  activeSubscription: TSubscriptions[0] | undefined;
  upcomingDrawDate: Date;
  loading?: boolean;
  showWinWithFriendsBadge?: boolean;
}

export const EntriesAndStats = ({
  isFtmp,
  tickets,
  tokenBalance,
  leaderboardPoints,
  activeSubscription,
  upcomingDrawDate,
  loading,
  showWinWithFriendsBadge,
}: EntriesAndStatsProps) => {
  const { isMobile } = useIsMobile();
  const days = activeSubscription?.drawSchedulePlans
    .map((draw) => draw.drawSchedule.code)
    .map((code) => code.toLowerCase())
    .map((code) => code.charAt(0).toUpperCase() + code.slice(1))
    .join(' and ');

  const choosePlanTitleReturningPlayer = 'Ready to jump back in?';
  const choosePlanTitleFTMPPlayer = 'Ready to get started?';

  // State logic
  const hasEntries = tickets.length > 0;
  const isActiveSubscriber = !!activeSubscription;
  const isCancelledSubscriber = !activeSubscription && !isFtmp;
  const isFtmpNoSubscription = isFtmp && !activeSubscription;
  const ticketConfiguration = activeSubscription?.drawOrder.entries; // if active subscription doesn't have active tickets, use drawOrder ticket configuration
  const configuredSubscriptionEntries = toLotteryEntries(ticketConfiguration);
  const { subscriptionEntries, addonEntries } =
    groupEntriesByPurchasingAction(tickets);
  const displaySubscriptionEntries =
    subscriptionEntries.length > 0
      ? subscriptionEntries
      : configuredSubscriptionEntries;
  const hasTicketConfiguration = configuredSubscriptionEntries.length > 0;
  const showReferralsBadge = showWinWithFriendsBadge && isActiveSubscriber;

  // Show/hide logic
  // Show entries if: active subscriber with entries, or cancelled subscriber with entries
  const showEntries =
    (hasEntries && (isActiveSubscriber || isCancelledSubscriber)) ||
    hasTicketConfiguration;

  const nextDrawDate = tickets.some((ticket) => ticket.draw?.drawDate)
    ? tickets.sort(
        (a, b) =>
          new Date(a.draw!.drawDate!).getTime() -
          new Date(b.draw!.drawDate!).getTime(),
      )[0].draw!.drawDate
    : undefined;

  // Show choose plan if: cancelled subscriber with no entries, or FTMP with no subscription and no entries
  const showChoosePlan =
    (!hasEntries && isCancelledSubscriber) ||
    (!hasEntries && isFtmpNoSubscription);
  const choosePlanTitle = isFtmpNoSubscription
    ? choosePlanTitleFTMPPlayer
    : choosePlanTitleReturningPlayer;

  const headingText = isActiveSubscriber
    ? 'Your next draw is in'
    : 'Next draw is in';

  const StatSkeleton = () => (
    <Skeleton
      width={'100%'}
      height={isMobile ? 129 : 192}
      containerClassName="flex-1"
      borderRadius={18}
    />
  );

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex' }}>
        {showChoosePlan && (
          <div className={`${styles.choosePlanContainer} desktopOnly`}>
            {loading && !isMobile ? (
              <div className={styles.choosePlanDesktopSkeleton}>
                <GenericSkeleton
                  height={150}
                  width={400}
                  borderRadius={24}
                  containerClassName="flex-1"
                />
              </div>
            ) : (
              <ChoosePlanSection title={choosePlanTitle} />
            )}
          </div>
        )}
        <div className={styles.entriesContainer}>
          {showChoosePlan && (
            <div className={`${styles.choosePlanContainer} mobileOnly`}>
              {loading && isMobile ? (
                <GenericSkeleton
                  inverseColors
                  height={140}
                  width={'100%'}
                  borderRadius={18}
                  containerClassName="flex-1"
                />
              ) : (
                <ChoosePlanSection title={choosePlanTitle} />
              )}
            </div>
          )}
          {showEntries && !showChoosePlan && (
            <div className={styles.entries}>
              <DashboardComponentHeader
                inverseSkeletonColors
                loading={loading}
                title={
                  <div className={styles.titleContainer}>
                    <div className={styles.ticketIcon}>
                      <ImageWithFallback
                        src={`${dashboardFolder}/ticket-icon.svg`}
                        layout="fill"
                      />
                    </div>
                    Your entries
                  </div>
                }
                subtitle={<>Entered every {days} at 5pm</>}
              />
              <TicketEntries
                subscriptionEntries={displaySubscriptionEntries}
                addonEntries={addonEntries}
                loading={loading}
              />
              <div className="mobileOnly">
                <ResultsLink loading={loading} />
              </div>
              {showReferralsBadge && loading ? (
                <div className={clsx('mobileOnly', styles.winWithFriendsBadge)}>
                  <GenericSkeleton
                    inverseColors
                    height={48}
                    width={'100%'}
                    borderRadius={18}
                  />
                </div>
              ) : showReferralsBadge && !loading ? (
                <div className={clsx('mobileOnly', styles.winWithFriendsBadge)}>
                  <WinWithFriendsBadge />
                </div>
              ) : null}
            </div>
          )}
          <div
            className={`${styles.separator} ${showChoosePlan ? 'mobileOnly' : ''}`}
          />
          <div
            className={styles.countdownContainer}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'stretch',
            }}
          >
            {showEntries && !showReferralsBadge && (
              <div style={{ flex: 0.3 }} />
            )}
            {showReferralsBadge && loading ? (
              <div className={clsx('desktopOnly', styles.winWithFriendsBadge)}>
                <GenericSkeleton
                  inverseColors
                  height={75}
                  width={'100%'}
                  borderRadius={24}
                />
              </div>
            ) : showReferralsBadge && !loading ? (
              <div className={clsx('desktopOnly', styles.winWithFriendsBadge)}>
                <WinWithFriendsBadge />
              </div>
            ) : null}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
              }}
            >
              {loading ? (
                <GenericSkeleton
                  inverseColors
                  height={130}
                  width={isMobile ? '100%' : 300}
                  borderRadius={isMobile ? 18 : 24}
                  containerClassName={isMobile ? 'flex-1' : ''}
                />
              ) : (
                <DrawCountdown
                  headingText={headingText}
                  targetDate={nextDrawDate ?? upcomingDrawDate}
                />
              )}
            </div>

            {showEntries && (
              <div className={`desktopOnly ${styles.resultsLinkWrapper}`}>
                <ResultsLink loading={loading} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.statsContainer}>
        {loading ? (
          <StatSkeleton />
        ) : (
          <StatSection
            isActiveSubscription={!!activeSubscription}
            onlyForSubscribedPlayer={
              STAT_SECTIONS.TOKENS.onlyForSubscribedPlayer
            }
            title={STAT_SECTIONS.TOKENS.title}
            icon={STAT_SECTIONS.TOKENS.icon}
            description={STAT_SECTIONS.TOKENS.description}
            total={tokenBalance}
            background={STAT_SECTIONS.TOKENS.background}
            backgroundDesktop={STAT_SECTIONS.TOKENS.backgroundDesktop}
            link={STAT_SECTIONS.TOKENS.link}
          />
        )}
        {loading ? (
          <StatSkeleton />
        ) : (
          <StatSection
            isActiveSubscription={!!activeSubscription}
            onlyForSubscribedPlayer={
              STAT_SECTIONS.DISCOUNTS.onlyForSubscribedPlayer
            }
            title={STAT_SECTIONS.DISCOUNTS.title}
            icon={STAT_SECTIONS.DISCOUNTS.icon}
            description={STAT_SECTIONS.DISCOUNTS.description}
            total={PartnerDiscounts.length}
            background={STAT_SECTIONS.DISCOUNTS.background}
            backgroundDesktop={STAT_SECTIONS.DISCOUNTS.backgroundDesktop}
            link={STAT_SECTIONS.DISCOUNTS.link}
          />
        )}
        {loading ? (
          <StatSkeleton />
        ) : (
          <StatSection
            isActiveSubscription={!!activeSubscription}
            onlyForSubscribedPlayer={
              STAT_SECTIONS.POINTS.onlyForSubscribedPlayer
            }
            title={STAT_SECTIONS.POINTS.title}
            icon={STAT_SECTIONS.POINTS.icon}
            description={STAT_SECTIONS.POINTS.description}
            background={STAT_SECTIONS.POINTS.background}
            backgroundDesktop={STAT_SECTIONS.POINTS.backgroundDesktop}
            total={leaderboardPoints}
            link={STAT_SECTIONS.POINTS.link}
          />
        )}
      </div>
    </div>
  );
};

// --- Store-bound Component ---

export const CurrentUserEntriesAndStats = observer(
  ({ loading, referrerName }: { loading?: boolean; referrerName?: string }) => {
    const {
      leaderboardStats,
      balances,
      activeWeeklyDrawTickets,
      activeSubscriptions,
      isFtmp,
      activeTicketsInitialized,
    } = useUserStore();

    const { upcomingDrawDate, loaded } = useDrawStore();
    const [entriesAndStatsLoading, setEntriesAndStatsLoading] = useState(true);

    const showWinWithFriendsBadge = !!referrerName;

    useEffect(() => {
      if (activeTicketsInitialized && loading === undefined) {
        return setEntriesAndStatsLoading(false);
      }

      if (activeTicketsInitialized && loaded && !loading) {
        setTimeout(() => setEntriesAndStatsLoading(false), LOADING_TIMEOUT);
      }
    }, [activeTicketsInitialized, loaded, loading]);

    if (
      !activeWeeklyDrawTickets ||
      !activeSubscriptions ||
      !balances ||
      !leaderboardStats ||
      !upcomingDrawDate
    ) {
      return null;
    }

    return (
      <AboutUsModule background="grey" withReducedPadding>
        <AboutUsSiteContainer overlapAbove>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <EntriesAndStats
              isFtmp={isFtmp}
              tickets={activeWeeklyDrawTickets}
              tokenBalance={balances?.tokenBalance || 0}
              leaderboardPoints={leaderboardStats?.pointsTotal || 0}
              activeSubscription={
                activeSubscriptions.length > 0
                  ? activeSubscriptions[0]
                  : undefined
              }
              upcomingDrawDate={upcomingDrawDate}
              loading={entriesAndStatsLoading}
              showWinWithFriendsBadge={showWinWithFriendsBadge}
            />
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>
    );
  },
);
